// Retry helper s exponential backoff a Sentry warning capture.
//
// Defaults: 3 pokusy, 500ms → 1500ms → 3500ms (cumulative ~5s max wait).
// Pre user-facing operations — long enough to recover from blips, short enough
// nech user nemyslí ze app je zaseknutá.
//
// Usage:
//   await withRetry(() => fetch(url), {
//     onRetry: ({ attempt, total, error }) => setStatus(`Skúšam znova... (${attempt}/${total})`),
//     shouldRetry: (err) => !(err instanceof DriveNotConnectedError),
//   });

import { addBreadcrumb, captureMessage } from '@/lib/observability';

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_DELAYS_MS = [500, 1500, 3500];

function defaultShouldRetry(_err) {
  return true;
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason || new Error('Aborted'));
    const id = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(id);
        reject(signal.reason || new Error('Aborted'));
      }, { once: true });
    }
  });
}

/**
 * Retry-wraps an async function with exponential backoff.
 *
 * @param {() => Promise<T>} fn - the async operation to retry
 * @param {Object} [opts]
 * @param {number} [opts.maxAttempts=3]
 * @param {number[]} [opts.delaysMs=[500,1500,3500]]
 * @param {(err: any) => boolean} [opts.shouldRetry] - return false to skip retry
 * @param {(info: { attempt: number, total: number, error: any }) => void} [opts.onRetry]
 * @param {string} [opts.label='operation'] - shows up in Sentry breadcrumb
 * @param {AbortSignal} [opts.signal]
 * @param {(err: any, attempt: number) => (number|undefined)} [opts.getRetryDelayMs] -
 *   server-driven backoff override. Return ms to wait (e.g. from an HTTP 429/503
 *   Retry-After header: `getRetryDelayMs: (err) => err.retryAfterMs`). Takes precedence
 *   over delaysMs and jitter. Return undefined to fall back to the default schedule.
 * @param {boolean} [opts.jitter=false] - randomize each delay to 0.5x–1.5x to avoid
 *   thundering herd when many clients retry in sync. Ignored when getRetryDelayMs returns a value.
 * @returns {Promise<T>}
 */
export async function withRetry(fn, opts = {}) {
  const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const delays = opts.delaysMs ?? DEFAULT_DELAYS_MS;
  const shouldRetry = opts.shouldRetry ?? defaultShouldRetry;
  const label = opts.label ?? 'operation';

  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (opts.signal?.aborted) throw opts.signal.reason || new Error('Aborted');

    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const isLast = attempt === maxAttempts;
      const giveUp = isLast || !shouldRetry(err);

      if (giveUp) {
        if (attempt > 1) {
          // ran out of attempts — capture as warning so we know retries didn't help
          captureMessage(`Retry exhausted: ${label}`, {
            level: 'warning',
            extra: { attempts: attempt, finalError: err?.message ?? String(err) },
          });
        }
        throw err;
      }

      // schedule retry
      addBreadcrumb({
        category: 'retry',
        level: 'info',
        message: `${label} attempt ${attempt} failed, retrying`,
        data: { error: err?.message ?? String(err) },
      });

      opts.onRetry?.({ attempt: attempt + 1, total: maxAttempts, error: err });

      let delay = delays[attempt - 1] ?? delays[delays.length - 1] ?? 1000;
      // server-driven backoff (HTTP 429/503 Retry-After) wins over the local schedule
      const override = opts.getRetryDelayMs?.(err, attempt);
      if (typeof override === 'number' && override >= 0) {
        delay = override;
      } else if (opts.jitter) {
        // full-ish jitter: 0.5x–1.5x, so synchronized clients don't retry in lockstep
        delay = Math.round(delay * (0.5 + Math.random()));
      }
      await sleep(delay, opts.signal);
    }
  }

  // unreachable
  throw lastErr;
}
